import { useTranslation } from "next-i18next";
import { useQueryClient } from "react-query";
import { useUpdateCommentMutation } from "src/api/client/comment-v2.api";
import { TComment } from "src/components/common/comments-area/comment/comment.types";
import { mapInfiniteQueryPages } from "src/components/common/comments-area/utils/map-infinite-query-pages";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { TCommentsPaginatedDataType } from "../utils/create-initial-replies-page-data";
import { updateCommentInfiniteQueryCache } from "../utils/update-comment-infinite-query-cache";

type TReplaceWithUpdatedComment = {
    comments: TComment[];
    updatedComment: TComment;
};

const replaceWithUpdatedComment = ({ comments, updatedComment }: TReplaceWithUpdatedComment): TComment[] =>
    comments.map(comment => {
        return comment.commentId === updatedComment.commentId ? updatedComment : comment;
    });

const useUpdateInfiniteQueryCacheUpdateComment = (entityId: string) => {
    const queryClient = useQueryClient();

    const updateComment = (updatedComment: TComment) => {
        const handleUpdatedComment = (data: TCommentsPaginatedDataType | undefined) => {
            if (data?.pages) {
                const mapCommentsCallback = (comments: TComment[]) => {
                    return replaceWithUpdatedComment({ comments, updatedComment });
                };

                return {
                    pages: mapInfiniteQueryPages({ pages: data.pages, callback: mapCommentsCallback }),
                    pageParams: data.pageParams,
                };
            }
        };

        updateCommentInfiniteQueryCache({
            queryClient,
            comment: updatedComment,
            entityId,
            handleRootComment: handleUpdatedComment,
            handleReplyComment: handleUpdatedComment,
        });
    };

    return { updateComment };
};

export const useUpdateComment = (entityId: string) => {
    const toast = useWfToast();
    const { t } = useTranslation(["common", "comment"]);

    const { updateComment } = useUpdateInfiniteQueryCacheUpdateComment(entityId);

    return useUpdateCommentMutation({
        onSuccess: newComment => {
            updateComment(newComment);

            toast({ status: EnumToastStatus.Success, title: t("toast.comment-saved-successfully", { ns: "comment" }) });
        },
        onError: () => {
            toast({ status: EnumToastStatus.Error, title: t("general-error") });
        },
    });
};
